//
// Copyright (c) 2021 Astute Global, Inc.
//

import {
  ButtonView,
  CssTransitionDisablerMixin,
  IconCancel,
  IconCheck,
  ListItemView,
  ListView,
  submitHandler,
  SwitchButtonView,
  View,
} from "ckeditor5";
// @ts-ignore
import i18next from "i18n/i18n";
import Messenger from "messaging/messenger";

import "../css/privilegedAreaView.css";
import { areaIdsToAreaNames } from "../util";

class BasePrivilegedAreaView extends View {
  constructor(editor, command) {
    super(editor.locale);
    this.editor = editor;
    this.command = command;
    this.allRoles = Messenger.reqres.request("kb:privileged:area:roles");
    this.privilegedAreaList = this._createPrivilegedAreaList();
    this.okButton = this._createOkButton();
    this.cancelButton = this._createCancelButton();
    this.setTemplate({
      tag: "form",
      attributes: {
        class: ["ck", "ck-responsive-form", "ck-vertical-form", "privileged-area-view"],
        tabindex: "-1",
      },
      children: this._createFormChildren(),
    });
  }

  focus() {
    this.privilegedAreaList.focus();
  }

  render() {
    super.render();
    submitHandler({
      view: this,
    });
  }

  _addPrivilegedArea(roleId) {
    const areaIds = this.command.areaIds;
    const roleIdString = roleId.toString();
    if (!areaIds) {
      this.command.areaIds = roleIdString;
      this.command.areaNames = areaIdsToAreaNames(this.editor, roleIdString);
    } else {
      const newAreaIdArray = [];
      const areaIdsArray = areaIds.split(",");
      for (const areaIdString of areaIdsArray) {
        if (areaIdString.trim() == roleIdString) {
          return;
        }
        newAreaIdArray.push(areaIdString);
      }
      newAreaIdArray.push(roleIdString);
      this.command.areaIds = newAreaIdArray.join(",");
      this.command.areaNames = areaIdsToAreaNames(this.editor, newAreaIdArray);
    }
  }

  _createCancelButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.cancel"),
      icon: IconCancel,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-cancel",
      },
    });
    button.delegate("execute").to(this, "cancel");
    return button;
  }

  _createFormChildren() {
    const children = this.createCollection();

    children.add(this.privilegedAreaList);
    children.add(this.okButton);
    children.add(this.cancelButton);

    return children;
  }

  _createOkButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.ok"),
      icon: IconCheck,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-save",
      },
    });
    button.type = "submit";
    button.delegate("execute").to(this, "submit");
    return button;
  }

  _createPrivilegedAreaList() {
    const listView = new ListView();
    this.allRoles.onSynced(() => {
      this.allRoles.each((role) => {
        const roleIdString = role.get("roleId").toString();
        const button = new SwitchButtonView(this.locale);
        button.set({
          name: roleIdString,
          label: role.get("roleName"),
          withText: true,
        });
        button.bind("isOn").to(this.command, "areaIds", (areaIds) => {
          if (!areaIds) {
            return false;
          }
          const areaIdsArray = areaIds.split(",");
          for (const areaId of areaIdsArray) {
            if (areaId.trim() == roleIdString) {
              return true;
            }
          }
          return false;
        });
        button.on("execute", () => {
          if (button.isOn) {
            this._removePrivilegedArea(roleIdString);
          } else {
            this._addPrivilegedArea(roleIdString);
          }
        });
        const listItemView = new ListItemView(this.locale);
        listItemView.children.add(button);
        listView.items.add(listItemView);
      });
    });
    return listView;
  }

  _removePrivilegedArea(roleId) {
    const areaIdsCsv = this.command.areaIds;
    if (!areaIdsCsv) {
      return;
    }
    const roleIdString = roleId.toString();
    const areaIdsArray = areaIdsCsv.split(",");
    const newAreaIdsArray = [];
    for (let i = 0; i < areaIdsArray.length; ++i) {
      const areaIdString = areaIdsArray[i].trim();
      if (areaIdString != roleIdString) {
        newAreaIdsArray.push(areaIdString);
      }
    }
    this.command.areaIds = newAreaIdsArray.join(",");
    this.command.areaNames = areaIdsToAreaNames(this.editor, newAreaIdsArray);
  }
}

const PrivilegedAreaView = CssTransitionDisablerMixin(BasePrivilegedAreaView);

export default PrivilegedAreaView;
